import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { roomMessageService, roomSocketService } from "../services/rooms";
import { toast } from "react-toastify";

const RoomMessagesContext = createContext();

export const useRoomMessages = () => {
  const context = useContext(RoomMessagesContext);
  if (!context) {
    throw new Error(
      "useRoomMessages must be used within a RoomMessagesProvider"
    );
  }
  return context;
};

export const RoomMessagesProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentRoomId, setCurrentRoomId] = useState(null);
  const [typingUsers, setTypingUsers] = useState([]);
  const [socketConnected, setSocketConnected] = useState(false);

  const socketInitialized = useRef(false);
  const typingTimeouts = useRef({});
  const pendingRoomRef = useRef(null); // Track pending room join at context level

  // Use ref to track cursor for pagination to avoid stale closure issues
  const cursorRef = useRef(null);
  const hasMoreRef = useRef(true);

  // Keep refs in sync with state
  useEffect(() => {
    cursorRef.current = cursor;
  }, [cursor]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  /**
   * Initialize socket connection listeners
   * Note: Socket is connected by socketManager in UserContext
   * This context just listens for events and manages state
   */
  useEffect(() => {
    if (socketInitialized.current) return;
    socketInitialized.current = true;

    // Set up event listeners - socket may already be connected or will connect later
    const setupListeners = () => {
      // Listen for socket connection
      const unsubConnect = roomSocketService.on("socket_connected", () => {
// Don't set socketConnected yet - wait for auth
      });

      const unsubAuth = roomSocketService.on("socket_authenticated", () => {
setSocketConnected(true);

        // If we have a pending room, join it now
        if (pendingRoomRef.current) {
roomSocketService.joinRoom(pendingRoomRef.current);
        }
      });

      const unsubDisconnect = roomSocketService.on(
        "socket_disconnected",
        () => {
setSocketConnected(false);
        }
      );

      return () => {
        unsubConnect();
        unsubAuth();
        unsubDisconnect();
      };
    };

    const cleanup = setupListeners();

    // Check if already authenticated (page refresh case where socket connected before context mounted)
    if (roomSocketService.isReady()) {
setSocketConnected(true);
    }

    return cleanup;
  }, []);

  /**
   * Load messages for a room
   */
  const loadMessages = useCallback(
    async (roomId, resetCursor = false) => {
      try {
        setLoading(true);

        // Use ref for cursor to avoid stale closure issues
        const cursorToUse = resetCursor ? null : cursorRef.current;
const data = await roomMessageService.getMessages(
          roomId,
          cursorToUse,
          50
        );
if (data.success) {
          // Update cursor ref IMMEDIATELY before any async operations
          const newCursor = data.pagination?.nextCursor || null;
          const newHasMore = data.pagination?.hasNext || false;
          cursorRef.current = newCursor;
          hasMoreRef.current = newHasMore;

          if (resetCursor) {
            // Remove duplicates
            const uniqueMessages = (data.data || []).filter(
              (msg, index, self) =>
                index === self.findIndex((m) => m._id === msg._id)
            );
setMessages(uniqueMessages);
          } else {
            // Prepend older messages for infinite scroll and remove duplicates
            setMessages((prev) => {
              const combined = [...(data.data || []), ...prev];
              const unique = combined.filter(
                (msg, index, self) =>
                  index === self.findIndex((m) => m._id === msg._id)
              );
return unique;
            });
          }

          // Also update state for UI rendering
          setCursor(newCursor);
          setHasMore(newHasMore);
return data;
        }
      } catch (error) {
toast.error("Failed to load messages");
      } finally {
        setLoading(false);
      }
    },
    [] // No dependencies - uses refs for mutable values
  );

  /**
   * Load pinned messages
   */
  const loadPinnedMessages = useCallback(async (roomId) => {
    try {
      const data = await roomMessageService.getPinnedMessages(roomId);
      if (data.success) {
        setPinnedMessages(data.data || []);
        return data;
      }
    } catch (error) { /* ignored */ }
  }, []);

  /**
   * Send a message
   * Uses socket if ready, falls back to REST API
   */
  const sendMessage = useCallback(
    async (roomId, content, replyToId = null, useSocket = true) => {
      try {
        // Use socket if ready and requested
        if (useSocket && roomSocketService.isReady()) {
          // Use socket for real-time messaging
          return new Promise((resolve, reject) => {
            roomSocketService.sendMessage(
              roomId,
              content,
              replyToId,
              (response) => {
                if (response.success) {
                  // Optimistically add message to local state
                  setMessages((prev) => {
                    // Avoid duplicates
                    if (prev.find((m) => m._id === response.data._id)) {
                      return prev;
                    }

                    // Enrich replyTo with full message data from existing messages
                    let enrichedMessage = response.data;
                    if (replyToId) {
                      const originalMessage = prev.find(
                        (m) => m._id === replyToId
                      );
                      if (originalMessage) {
                        enrichedMessage = {
                          ...response.data,
                          replyTo: {
                            _id: originalMessage._id,
                            content: originalMessage.content,
                            sender: originalMessage.sender,
                          },
                        };
                      }
                    }

                    return [...prev, enrichedMessage];
                  });
                  resolve(response);
                } else {
                  reject(new Error(response.message || "Failed to send"));
                }
              }
            );
          });
        } else {
          // Fall back to REST API (works even when socket not ready)
const data = await roomMessageService.sendMessage(
            roomId,
            content,
            replyToId
          );

          if (data.success) {
            // Add message to local state with enriched replyTo
            setMessages((prev) => {
              let enrichedMessage = data.data;
              if (replyToId) {
                const originalMessage = prev.find((m) => m._id === replyToId);
                if (originalMessage) {
                  enrichedMessage = {
                    ...data.data,
                    replyTo: {
                      _id: originalMessage._id,
                      content: originalMessage.content,
                      sender: originalMessage.sender,
                    },
                  };
                }
              }
              return [...prev, enrichedMessage];
            });
            return data;
          }
        }
      } catch (error) {
toast.error(error.message || "Failed to send message");
        throw error;
      }
    },
    [socketConnected]
  );

  /**
   * Edit a message
   */
  const editMessage = useCallback(async (roomId, messageId, newContent) => {
    try {
      const data = await roomMessageService.editMessage(
        roomId,
        messageId,
        newContent
      );

      if (data.success) {
        // Update message in local state
        setMessages((prev) =>
          prev.map((msg) =>
            msg._id === messageId
              ? {
                  ...msg,
                  content: newContent,
                  isEdited: true,
                  editedAt: data.data.editedAt,
                }
              : msg
          )
        );

        toast.success("Message edited");
        return data;
      }
    } catch (error) {
toast.error(error.message || "Failed to edit message");
      throw error;
    }
  }, []);

  /**
   * Delete a message
   */
  const deleteMessage = useCallback(async (roomId, messageId) => {
    try {
      const data = await roomMessageService.deleteMessage(roomId, messageId);

      if (data.success) {
        // Mark as deleted in local state
        setMessages((prev) =>
          prev.map((msg) =>
            msg._id === messageId
              ? { ...msg, isDeleted: true, content: null }
              : msg
          )
        );

        toast.success("Message deleted");
        return data;
      }
    } catch (error) {
toast.error(error.message || "Failed to delete message");
      throw error;
    }
  }, []);

  /**
   * Add a reaction
   */
  const addReaction = useCallback(async (roomId, messageId, emoji) => {
    try {
      const data = await roomMessageService.addReaction(
        roomId,
        messageId,
        emoji
      );

      if (data.success) {
        // Update message reactions in local state
        setMessages((prev) =>
          prev.map((msg) =>
            msg._id === messageId
              ? {
                  ...msg,
                  reactions: data.data.reactions,
                  reactionSummary: data.data.reactionSummary,
                }
              : msg
          )
        );

        return data;
      }
    } catch (error) {
toast.error("Failed to add reaction");
      throw error;
    }
  }, []);

  /**
   * Remove a reaction
   */
  const removeReaction = useCallback(async (roomId, messageId, emoji) => {
    try {
      const data = await roomMessageService.removeReaction(
        roomId,
        messageId,
        emoji
      );

      if (data.success) {
        // Update message reactions in local state
        setMessages((prev) =>
          prev.map((msg) =>
            msg._id === messageId
              ? {
                  ...msg,
                  reactions: data.data.reactions,
                  reactionSummary: data.data.reactionSummary,
                }
              : msg
          )
        );

        return data;
      }
    } catch (error) {
throw error;
    }
  }, []);

  /**
   * Pin a message
   */
  const pinMessage = useCallback(
    async (roomId, messageId) => {
      try {
        const data = await roomMessageService.pinMessage(roomId, messageId);

        if (data.success) {
          toast.success("Message pinned");
          // Reload pinned messages
          await loadPinnedMessages(roomId);
          return data;
        }
      } catch (error) {
toast.error(error.message || "Failed to pin message");
        throw error;
      }
    },
    [loadPinnedMessages]
  );

  /**
   * Unpin a message
   */
  const unpinMessage = useCallback(
    async (roomId, messageId) => {
      try {
        const data = await roomMessageService.unpinMessage(roomId, messageId);

        if (data.success) {
          toast.success("Message unpinned");
          // Reload pinned messages
          await loadPinnedMessages(roomId);
          return data;
        }
      } catch (error) {
toast.error(error.message || "Failed to unpin message");
        throw error;
      }
    },
    [loadPinnedMessages]
  );

  /**
   * Search messages
   */
  const searchMessages = useCallback(async (roomId, query, limit = 20) => {
    try {
      const data = await roomMessageService.searchMessages(
        roomId,
        query,
        limit
      );

      if (data.success) {
        return data;
      }
    } catch (error) {
toast.error("Search failed");
      throw error;
    }
  }, []);

  /**
   * Mark messages as read
   */
  const markAsRead = useCallback(async (roomId, messageIds = []) => {
    try {
      await roomMessageService.markMessagesAsRead(roomId, messageIds);
    } catch (error) { /* ignored */ }
  }, []);

  /**
   * Report a message
   */
  const reportMessage = useCallback(async (roomId, messageId, reportData) => {
    try {
      const data = await roomMessageService.reportMessage(
        roomId,
        messageId,
        reportData
      );

      if (data.success) {
        toast.success("Message reported successfully");
        return data;
      }
    } catch (error) {
toast.error("Failed to report message");
      throw error;
    }
  }, []);

  /**
   * Start typing indicator
   */
  const startTyping = useCallback((roomId) => {
    if (roomSocketService.isReady()) {
      roomSocketService.startTyping(roomId);
    }
  }, []);

  /**
   * Stop typing indicator
   */
  const stopTyping = useCallback((roomId) => {
    if (roomSocketService.isReady()) {
      roomSocketService.stopTyping(roomId);
    }
  }, []);

  /**
   * Join a room (socket and load messages)
   * Handles both immediate join and delayed join (waiting for socket auth)
   */
  const joinRoom = useCallback(
    async (roomId) => {
setCurrentRoomId(roomId);
      pendingRoomRef.current = roomId; // Track at context level too

      // Reset pagination refs when joining a new room
      cursorRef.current = null;
      hasMoreRef.current = true;
      setCursor(null);
      setHasMore(true);

      // Always attempt to join - the socket service will queue if not ready
      const joined = roomSocketService.joinRoom(roomId);

      if (joined) {
        pendingRoomRef.current = null;
      }

      // Load messages and pinned messages (this works via REST API regardless of socket)
      await Promise.all([
        loadMessages(roomId, true),
        loadPinnedMessages(roomId),
      ]);
    },
    [loadMessages, loadPinnedMessages]
  );

  /**
   * Leave a room
   */
  const leaveRoom = useCallback((roomId) => {
pendingRoomRef.current = null; // Clear pending

    if (roomSocketService.isConnected()) {
      roomSocketService.leaveRoom(roomId);
    }

    // Clear state and refs
    setMessages([]);
    setPinnedMessages([]);
    setCursor(null);
    cursorRef.current = null;
    setHasMore(true);
    hasMoreRef.current = true;
    setTypingUsers([]);
    setCurrentRoomId(null);
  }, []);

  /**
   * Setup socket event listeners for real-time updates
   */
  useEffect(() => {
    if (!socketConnected) return;

    // New message received
    const unsubNewMessage = roomSocketService.on("new_message", (message) => {
setMessages((prev) => {
        // Avoid duplicates
        if (prev.find((m) => m._id === message._id)) {
          return prev;
        }

        // Enrich replyTo with full message data if needed
        let enrichedMessage = message;
        if (message.replyTo && typeof message.replyTo === "object") {
          // If replyTo exists but content is missing, find it from existing messages
          if (!message.replyTo.content && message.replyTo._id) {
            const originalMessage = prev.find(
              (m) => m._id === message.replyTo._id
            );
if (originalMessage) {
              enrichedMessage = {
                ...message,
                replyTo: {
                  _id: originalMessage._id,
                  content: originalMessage.content,
                  sender: originalMessage.sender,
                },
              };
}
          }
        } else if (typeof message.replyTo === "string") {
          // If replyTo is just an ID string, find the full message
          const originalMessage = prev.find((m) => m._id === message.replyTo);
if (originalMessage) {
            enrichedMessage = {
              ...message,
              replyTo: {
                _id: originalMessage._id,
                content: originalMessage.content,
                sender: originalMessage.sender,
              },
            };
}
        }

        return [...prev, enrichedMessage];
      });
    });

    // Message edited
    const unsubEdited = roomSocketService.on("message_edited", (data) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg._id === data.messageId
            ? {
                ...msg,
                content: data.content,
                isEdited: true,
                editedAt: data.editedAt,
              }
            : msg
        )
      );
    });

    // Message deleted - now includes deletedByName from backend
    const unsubDeleted = roomSocketService.on("message_deleted", (data) => {
setMessages((prev) =>
        prev.map((msg) =>
          msg._id === data.messageId
            ? {
                ...msg,
                isDeleted: true,
                content: null,
                deletedByName: data.deletedByName,
              }
            : msg
        )
      );
    });

    // Reaction added
    const unsubReactionAdded = roomSocketService.on(
      "reaction_added",
      (data) => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg._id === data.messageId
              ? {
                  ...msg,
                  reactions: data.reactions,
                  reactionSummary: data.reactionSummary,
                }
              : msg
          )
        );
      }
    );

    // Reaction removed
    const unsubReactionRemoved = roomSocketService.on(
      "reaction_removed",
      (data) => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg._id === data.messageId
              ? {
                  ...msg,
                  reactions: data.reactions,
                  reactionSummary: data.reactionSummary,
                }
              : msg
          )
        );
      }
    );

    // Message pinned
    const unsubPinned = roomSocketService.on("message_pinned", (data) => {
      if (currentRoomId) {
        loadPinnedMessages(currentRoomId);
      }
    });

    // Message unpinned
    const unsubUnpinned = roomSocketService.on("message_unpinned", (data) => {
      if (currentRoomId) {
        loadPinnedMessages(currentRoomId);
      }
    });

    // Typing indicators - now stores { id, name } objects instead of just userId
    const unsubTyping = roomSocketService.on("user_typing", (data) => {
setTypingUsers((prev) => {
        // Check if user already in typing list
        if (prev.find((u) => u.id === data.userId)) return prev;
        // Backend sends userName (first name)
        const name = data.userName || data.firstName || data.name || "Someone";
        return [...prev, { id: data.userId, name: name }];
      });

      // Clear timeout if exists
      if (typingTimeouts.current[data.userId]) {
        clearTimeout(typingTimeouts.current[data.userId]);
      }

      // Auto-remove after 3 seconds
      typingTimeouts.current[data.userId] = setTimeout(() => {
        setTypingUsers((prev) => prev.filter((u) => u.id !== data.userId));
        delete typingTimeouts.current[data.userId];
      }, 3000);
    });

    const unsubStopTyping = roomSocketService.on("user_stop_typing", (data) => {
      setTypingUsers((prev) => prev.filter((u) => u.id !== data.userId));
      if (typingTimeouts.current[data.userId]) {
        clearTimeout(typingTimeouts.current[data.userId]);
        delete typingTimeouts.current[data.userId];
      }
    });

    // Cleanup
    return () => {
      unsubNewMessage();
      unsubEdited();
      unsubDeleted();
      unsubReactionAdded();
      unsubReactionRemoved();
      unsubPinned();
      unsubUnpinned();
      unsubTyping();
      unsubStopTyping();

      // Clear all typing timeouts
      Object.values(typingTimeouts.current).forEach(clearTimeout);
      typingTimeouts.current = {};
    };
  }, [socketConnected, currentRoomId, loadPinnedMessages]);

  const value = {
    // State
    messages,
    pinnedMessages,
    loading,
    hasMore,
    cursor,
    currentRoomId,
    typingUsers,
    socketConnected,

    // Actions
    loadMessages,
    loadPinnedMessages,
    sendMessage,
    editMessage,
    deleteMessage,
    addReaction,
    removeReaction,
    pinMessage,
    unpinMessage,
    searchMessages,
    markAsRead,
    reportMessage,
    startTyping,
    stopTyping,
    joinRoom,
    leaveRoom,

    // Direct setters (use sparingly)
    setMessages,
    setPinnedMessages,
    setLoading,
  };

  return (
    <RoomMessagesContext.Provider value={value}>
      {children}
    </RoomMessagesContext.Provider>
  );
};

export default RoomMessagesContext;
